import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';

import { computeEffectiveWordCeiling } from './authorWordBudget';
import { isRtlTextDir } from './rtlEditorAlign';

/** Bump when TipTap registrations in `getAuthoredEditorExtensions` change (invalidates `useMemo` extension lists). */
export const AUTHORED_EDITOR_EXTENSION_SET_VERSION = 9;

/**
 * Tokens across the whole document (headings, tables, code blocks, body).
 * Do not use `doc.textContent` — ProseMirror joins blocks with no separator, so
 * adjacent one-word headings become a single bogus “word” (e.g. header1header2header3).
 */
export function countWordsFromDoc(doc) {
  if (!doc) return 0;
  let n = 0;
  doc.descendants((node) => {
    if (node.isText) n += wordCountTrimmed(node.text);
  });
  return n;
}

function wordCountTrimmed(text) {
  if (!text || !String(text).trim()) return 0;
  return String(text).trim().split(/\s+/).filter(Boolean).length;
}

/** Words outside of fenced code blocks — used vs effective prose ceiling (code size already debits ceiling). */
export function countProseWordsFromDoc(doc) {
  if (!doc) return 0;
  let n = 0;
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    const $pos = doc.resolve(pos);
    for (let d = $pos.depth; d > 0; d -= 1) {
      if ($pos.node(d).type.name === 'codeBlock') return;
    }
    n += wordCountTrimmed(node.text);
  });
  return n;
}

function countNodesNamed(doc, typeName) {
  let c = 0;
  doc.descendants((node) => {
    if (node.type.name === typeName) c += 1;
  });
  return c;
}

const AuthorWordCeiling = Extension.create({
  name: 'authorWordCeiling',
  addOptions() {
    return {
      getBaseMaxWords: () => 240,
      shouldSkipValidation: undefined,
    };
  },
  addProseMirrorPlugins() {
    const getBaseMaxWords = this.options.getBaseMaxWords;
    const shouldSkipValidation = this.options.shouldSkipValidation;

    return [
      new Plugin({
        filterTransaction(transaction, state) {
          if (!transaction.docChanged) return true;
          const base = typeof getBaseMaxWords === 'function' ? getBaseMaxWords() : 240;
          if (base <= 0) return true;
          if (shouldSkipValidation?.()) return true;

          const ceiling = computeEffectiveWordCeiling(base, transaction.doc);
          const oldCount = countProseWordsFromDoc(state.doc);
          const newCount = countProseWordsFromDoc(transaction.doc);

          if (newCount <= ceiling) return true;
          // Allow shaving prose when already over cap (policy / structure change).
          if (newCount < oldCount) return true;

          return false;
        },
      }),
    ];
  },
});

const AuthorComposeGuard = Extension.create({
  name: 'authorComposeGuard',
  addOptions() {
    return {
      getGuards: () => ({ blockTable: false, blockCodeBlock: false }),
      /** Same hook as AuthorWordCeiling — bypass during programmatic `setContent` (e.g. page switch). */
      shouldSkipValidation: undefined,
    };
  },
  addProseMirrorPlugins() {
    const getGuards = this.options.getGuards;
    const shouldSkipValidation = this.options.shouldSkipValidation;

    return [
      new Plugin({
        filterTransaction(transaction, state) {
          if (!transaction.docChanged) return true;
          if (typeof shouldSkipValidation === 'function' && shouldSkipValidation()) return true;
          const { blockTable, blockCodeBlock } =
            typeof getGuards === 'function' ? getGuards() : {};

          const oldDoc = state.doc;
          const newDoc = transaction.doc;

          // One table per document — reject paste/splits that would create a second table.
          if (countNodesNamed(newDoc, 'table') > 1) return false;

          if (blockTable) {
            if (countNodesNamed(newDoc, 'table') > countNodesNamed(oldDoc, 'table')) return false;
            if (countNodesNamed(newDoc, 'tableRow') > countNodesNamed(oldDoc, 'tableRow'))
              return false;
          }
          if (blockCodeBlock) {
            if (countNodesNamed(newDoc, 'codeBlock') > countNodesNamed(oldDoc, 'codeBlock'))
              return false;
          }

          return true;
        },
      }),
    ];
  },
});

export function getAuthoredEditorExtensions({
  editable,
  placeholder = 'Write here…',
  getBaseMaxWords,
  /** @deprecated use getBaseMaxWords */
  maxWords,
  shouldSkipWordLimit,
  getComposeGuards,
  /** 'rtl' | 'ltr' — default paragraph alignment uses logical start (right in RTL). */
  textDir = 'ltr',
} = {}) {
  const resolveBaseMax =
    typeof getBaseMaxWords === 'function'
      ? getBaseMaxWords
      : typeof maxWords === 'number'
        ? () => maxWords
        : () => 240;

  const defaultAlignment = isRtlTextDir(textDir) ? 'right' : 'left';

  const extensions = [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph', 'bulletList', 'orderedList', 'blockquote'],
      alignments: ['left', 'center', 'right'],
      defaultAlignment,
    }),
    Underline,
    Table.configure({
      resizable: Boolean(editable),
    }),
    TableRow,
    TableHeader,
    TableCell,
    Placeholder.configure({
      placeholder,
      showOnlyWhenEditable: true,
    }),
  ];

  if (editable) {
    extensions.push(
      AuthorWordCeiling.configure({
        getBaseMaxWords: resolveBaseMax,
        shouldSkipValidation: shouldSkipWordLimit,
      }),
    );
    extensions.push(
      AuthorComposeGuard.configure({
        getGuards: typeof getComposeGuards === 'function' ? getComposeGuards : () => ({}),
        shouldSkipValidation: shouldSkipWordLimit,
      }),
    );
  }

  return extensions;
}
